import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import {
  FacetCount,
  FulfillmentState,
  GuaranteeDecision,
  GuaranteeProcessingState,
  GuaranteesFacetCounts,
  GuaranteesFilters,
  GuaranteesRangePreset,
  GuaranteesViewMode,
  Icd10FacetView,
  RANGE_PRESETS,
  ServiceKind,
  VIEW_MODES,
} from '@lib/guarantees/data-access';
import { selectGuaranteesMemberId } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { CHECK_ROW_CLASS, CheckBoxComponent } from '@lib/guarantees/feature/components/common/check-box.component';
import { RANGE_PRESET_LABEL_KEY } from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import { SEARCH_DEBOUNCE_MS } from '@lib/guarantees/feature/components/common/search-field.component';
import { ToggleDirective } from '@lib/guarantees/feature/components/common/toggle.directive';
import { Icd10FilterComponent } from '@lib/guarantees/feature/components/icd10-filter/icd10-filter.component';
import { BUTTON_CLASSES, MENU_CLASSES } from '@lib/guarantees/feature/components/ui/ui.classes';
import { openGuaranteeForm } from '@lib/guarantees/feature/operations/guarantee-form.component';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';

/**
 * What the side panel is reading. The two work panels are opened from this toolbar; the two record
 * panels are opened from the stream, and are listed here because one panel is open at a time.
 */
export type GuaranteesSidePanel = 'cases' | 'breaches' | 'guarantee' | 'prescription';

/** A foldable group of the filter panel. */
type FilterGroup = 'decision' | 'kind' | 'fulfillment' | 'status' | 'icd10';

/**
 * One foldable group of the panel. Describing them rather than writing each one out is what lets the
 * header, its badge, its rows, the filter-button count and the inline chips all derive from one
 * table instead of repeating the same five-way split.
 */
interface FilterGroupDescriptor {
  id: FilterGroup;
  labelKey: string;
  /** Prefixes a value to name it. Absent on the diagnosis tree, whose codes are their own names. */
  valueLabelPrefix?: string;
  hint?: string;
  /** What the group currently holds — its badge, its checked rows and its inline chips. */
  selection: (filters: GuaranteesFilters) => readonly string[];
  /** What it offers to pick from. Absent on the diagnosis tree, which draws its own rows. */
  facets?: (facets: GuaranteesFacetCounts) => readonly FacetCount<string>[];
}

/** Every group, in the order the panel offers them. */
const FILTER_GROUPS: readonly FilterGroupDescriptor[] = [
  {
    id: 'decision',
    labelKey: 'guaranteeV2.label.decisionFacet',
    valueLabelPrefix: 'guaranteeV2.label.decision',
    selection: filters => filters.decisions,
    facets: facets => facets.decisions,
  },
  {
    id: 'kind',
    labelKey: 'guaranteeV2.label.kindFacet',
    valueLabelPrefix: 'guaranteeV2.label.kind',
    selection: filters => filters.kinds,
    facets: facets => facets.kinds,
  },
  {
    id: 'fulfillment',
    labelKey: 'guaranteeV2.label.fulfillmentFacet',
    valueLabelPrefix: 'guaranteeV2.label.fulfillment',
    selection: filters => filters.fulfillments,
    facets: facets => facets.fulfillments,
  },
  {
    id: 'status',
    labelKey: 'guaranteeV2.label.statusFacet',
    valueLabelPrefix: 'guaranteeV2.label.status',
    selection: filters => filters.statuses,
    // A single status is not a choice, so the group withholds its one value.
    facets: facets => (facets.statuses.length > 1 ? facets.statuses : []),
  },
  {
    id: 'icd10',
    labelKey: 'guaranteeV2.label.icd10Class',
    hint: 'guaranteeV2.message.icd10FilterHint',
    selection: filters => filters.icd10Prefixes,
  },
];

/** A facet selection rendered inline, so active filter state is always visible. */
interface ActiveFilter {
  /** `deleted` is not a group: it is one switch, and it reports itself as a chip like the rest. */
  facet: FilterGroup | 'deleted';
  value: string;
  /** Absent on a diagnosis branch: a code is its own name and has nothing to translate. */
  labelKey?: string;
}

/** Only the diagnosis tree folds by default: it browses, and would take the whole panel open. */
const COLLAPSED_GROUPS: readonly FilterGroup[] = ['icd10'];

const VIEW_MODE_LABEL_KEYS: Record<GuaranteesViewMode, string> = {
  timeline: 'guaranteeV2.label.modeTimeline',
  prescriptions: 'guaranteeV2.label.modePrescriptions',
  decisions: 'guaranteeV2.label.modeQueue',
  delivery: 'guaranteeV2.label.modeDelivery',
};

const VIEW_MODE_HINT_KEYS: Record<GuaranteesViewMode, string> = {
  timeline: 'guaranteeV2.message.modeTimelineHint',
  prescriptions: 'guaranteeV2.message.modePrescriptionsHint',
  decisions: 'guaranteeV2.message.modeQueueHint',
  delivery: 'guaranteeV2.message.modeDeliveryHint',
};

const VIEW_MODE_ICONS: Record<GuaranteesViewMode, string> = {
  timeline: 'view_timeline',
  prescriptions: 'table_rows',
  decisions: 'fact_check',
  delivery: 'money_off',
};

/**
 * One-line toolbar. Facets and the date range live in popovers and only the selections a
 * health manager has actually made are rendered inline, so the header stays a single row
 * until it has something to report and never needs horizontal scrolling.
 */
@Component({
  selector: 'guarantees-toolbar',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    TranslatePipe,
    CheckBoxComponent,
    Icd10FilterComponent,
    ToggleDirective,
  ],
  templateUrl: './guarantees-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuaranteesToolbarComponent {
  protected readonly checkRow = CHECK_ROW_CLASS;

  /** Dropdown chrome, so a menu opened from a row reads as part of the same page. */
  protected readonly menu = MENU_CLASSES;

  /** The module's button vocabulary, so the stream's controls and the panels' are the same controls. */
  protected readonly buttons = BUTTON_CLASSES;

  filters = input.required<GuaranteesFilters>();
  facets = input.required<GuaranteesFacetCounts>();
  icd10Facets = input.required<Icd10FacetView>();
  rangePreset = input.required<GuaranteesRangePreset>();
  viewMode = input.required<GuaranteesViewMode>();
  /** What each work list currently holds, so a mode says how much is in it before it is opened. */
  queueCount = input<number>(0);
  deliveryGapCount = input<number>(0);
  /** Services after filtering — what the flat table would show. */
  prescriptionCount = input<number>(0);
  /** Visits after filtering. */
  visitCount = input<number>(0);
  breachCount = input<number>(0);
  /** Every case the member has, closed ones included — the panel opens on those when it must. */
  caseCount = input<number>(0);
  /** Which panel is open, so the button that opens it reads as pressed. */
  sidePanel = input<GuaranteesSidePanel | null>(null);
  /** Whether deleted records are being projected. */
  showDeleted = input<boolean>(false);
  busy = input<boolean>(false);

  searchChanged = output<string>();
  rangePresetChanged = output<GuaranteesRangePreset>();
  viewModeChanged = output<GuaranteesViewMode>();
  decisionToggled = output<GuaranteeDecision>();
  kindToggled = output<ServiceKind>();
  fulfillmentToggled = output<FulfillmentState>();
  statusToggled = output<GuaranteeProcessingState>();
  icd10PrefixToggled = output<string>();
  icd10CursorChanged = output<string | null>();
  icd10Cleared = output<void>();
  filtersCleared = output<void>();
  showDeletedToggled = output<void>();
  panelToggled = output<GuaranteesSidePanel>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  /** The member the slice holds — who a raised guarantee belongs to, and whose visits it offers. */
  private readonly memberId = this.store.selectSignal(selectGuaranteesMemberId);

  /** Withholds every control that writes, for a portal that may read the record but not change it. */
  readOnly = input<boolean>(false);

  /** Raising a record needs someone to raise it on, so the button waits for the member. */
  protected readonly canCreate = computed(() => !this.readOnly() && !!this.memberId());

  protected createGuarantee(): void {
    const memberId = this.memberId();
    if (!memberId) {
      return;
    }

    openGuaranteeForm(this.dialog, this.store, { mode: 'create', memberId });
  }

  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });
  /**
   * What is typed right now, as opposed to `filters().search`, which is what is actually filtering.
   * The clear button answers to this one so it appears on the first keystroke rather than after the
   * debounce; it is a signal because the control's own value is not one, and this view is OnPush.
   */
  protected readonly searchValue = signal('');
  protected readonly activeRangeLabel = computed(() => RANGE_PRESET_LABEL_KEY[this.rangePreset()]);

  protected readonly activeViewModeLabel = computed(() => VIEW_MODE_LABEL_KEYS[this.viewMode()]);
  protected readonly activeViewModeIcon = computed(() => VIEW_MODE_ICONS[this.viewMode()]);

  /**
   * Each mode row resolved in one pass — its labels, its icon, whether it is current and how much it
   * holds. Zero is left unsaid: an empty work list is reported by opening it, not by a badge reading
   * nothing.
   */
  protected readonly viewModeRows = computed(() => {
    const active = this.viewMode();
    const counts: Record<GuaranteesViewMode, number> = {
      timeline: 0,
      prescriptions: this.prescriptionCount(),
      decisions: this.queueCount(),
      delivery: this.deliveryGapCount(),
    };

    return VIEW_MODES.map(mode => ({
      mode,
      labelKey: VIEW_MODE_LABEL_KEYS[mode],
      hintKey: VIEW_MODE_HINT_KEYS[mode],
      icon: VIEW_MODE_ICONS[mode],
      active: mode === active,
      count: counts[mode],
    }));
  });

  protected readonly rangeRows = computed(() => {
    const active = this.rangePreset();

    return RANGE_PRESETS.map(preset => ({ preset, labelKey: RANGE_PRESET_LABEL_KEY[preset], active: preset === active }));
  });

  /**
   * Every selection across every group, in panel order. The chips render from it and the badge on
   * the filter button counts it — diagnosis branches included, since every group lives inside that
   * one dropdown and the badge is the only thing outside it saying something is set.
   */
  protected readonly activeFilters = computed<ActiveFilter[]>(() => {
    const filters = this.filters();

    return [
      ...FILTER_GROUPS.flatMap(group =>
        group.selection(filters).map<ActiveFilter>(value => ({
          facet: group.id,
          value,
          labelKey: group.valueLabelPrefix ? group.valueLabelPrefix + value : undefined,
        }))
      ),
      // Reported beside the facets because it is read the same way: something is on that the stream
      // would not be showing otherwise, and this row is where that is turned back off.
      ...(this.showDeleted() ? [{ facet: 'deleted' as const, value: 'deleted', labelKey: 'guaranteeV2.action.showDeleted' }] : []),
    ];
  });

  protected readonly activeFacetCount = computed(() => this.activeFilters().length);

  /** Whether the member's window holds any diagnosis at all — the group has nothing to show if not. */
  protected readonly hasIcd10 = computed(() => this.icd10Facets().children.length > 0 || this.icd10Facets().selection.length > 0);

  /** Groups the health manager has folded away, plus the one that starts that way. */
  private readonly collapsedGroups = signal<ReadonlySet<FilterGroup>>(new Set(COLLAPSED_GROUPS));

  protected toggleGroup(group: FilterGroup): void {
    this.collapsedGroups.update(groups => {
      const next = new Set(groups);
      if (!next.delete(group)) {
        next.add(group);
      }

      return next;
    });
  }

  /**
   * The panel resolved in one pass: which groups are offered, whether each is open, what it holds
   * while folded, and every row already carrying its label and its checked state. The template then
   * only reads properties, so nothing re-derives per binding on each change-detection run.
   */
  protected readonly filterGroups = computed(() => {
    const facets = this.facets();
    const filters = this.filters();
    const collapsed = this.collapsedGroups();

    return FILTER_GROUPS.map(group => {
      const selection = group.selection(filters);

      return {
        id: group.id,
        labelKey: group.labelKey,
        hint: group.hint,
        /** The diagnosis tree browses rather than lists, so it draws its own rows. */
        isTree: !group.facets,
        open: !collapsed.has(group.id),
        count: selection.length,
        rows: (group.facets?.(facets) ?? []).map(facet => ({
          value: facet.value,
          count: facet.count,
          labelKey: group.valueLabelPrefix + facet.value,
          active: selection.includes(facet.value),
        })),
      };
    }).filter(group => (group.isTree ? this.hasIcd10() : group.rows.length > 0));
  });

  /** The term actually narrowing the stream. Trimmed, so a box holding only spaces reports nothing. */
  protected readonly appliedSearch = computed(() => this.filters().search.trim());

  constructor() {
    const typed$ = this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
    typed$.subscribe(value => this.searchValue.set(value));
    // Only the settled term reaches the slice; every keystroke would reproject the whole stream.
    typed$.pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged()).subscribe(value => this.searchChanged.emit(value));

    // The slice owns the applied term, so a reset dispatched from anywhere else — the empty state's
    // own clear button, for one — empties the box too instead of leaving it contradicting the stream.
    effect(() => {
      const search = this.filters().search;
      if (search !== this.searchControl.value) {
        this.searchControl.setValue(search, { emitEvent: false });
        this.searchValue.set(search);
      }
    });
  }

  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  /**
   * Selecting a facet and clearing the chip that reports it are the same write, so a row in the
   * panel and a chip in the header both come through here.
   */
  protected toggleFacet(group: FilterGroup | 'deleted', value: string): void {
    switch (group) {
      case 'deleted':
        this.showDeletedToggled.emit();
        break;
      case 'decision':
        this.decisionToggled.emit(value as GuaranteeDecision);
        break;
      case 'kind':
        this.kindToggled.emit(value as ServiceKind);
        break;
      case 'fulfillment':
        this.fulfillmentToggled.emit(value as FulfillmentState);
        break;
      case 'status':
        this.statusToggled.emit(value as GuaranteeProcessingState);
        break;
      case 'icd10':
        this.icd10PrefixToggled.emit(value);
        break;
    }
  }

  /**
   * Emitted rather than set silently. A term typed within the debounce is still in flight, and a
   * reset the stream never sees is overtaken by it: the pending keystroke lands a quarter-second
   * later, re-applies the search the manager has just cleared, and the effect below writes it back
   * into a box they watched go empty. Passing the empty value through the same stream supersedes it.
   */
  protected clearAll(): void {
    this.searchControl.setValue('');
    this.filtersCleared.emit();
  }
}
